import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Clock, Calendar, ArrowRight, Loader2, BookOpen } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import api from "@/lib/api";

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return ""; }
}

function BlogCard({ post, featured }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className={`group block rounded-2xl overflow-hidden border border-stone-200 bg-white hover:border-indigo-200 hover:shadow-lg transition-all duration-200 ${featured ? "lg:col-span-2" : ""}`}
      data-testid={`blog-card-${post.slug}`}
    >
      {post.cover_image_url && (
        <div className={`overflow-hidden bg-stone-100 ${featured ? "h-56 lg:h-72" : "h-44"}`}>
          <img
            src={post.cover_image_url}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
      )}
      <div className="p-6">
        <div className="flex items-center gap-4 text-xs text-stone-400 mb-3">
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />{fmt(post.published_at)}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />{post.reading_time} min read
          </span>
        </div>
        <h2 className={`font-display text-stone-900 leading-snug group-hover:text-indigo-700 transition-colors ${featured ? "text-2xl lg:text-3xl" : "text-lg"}`}>
          {post.title}
        </h2>
        <p className="text-stone-500 text-sm mt-2 line-clamp-2">{post.meta_description}</p>
        <span className="mt-4 inline-flex items-center gap-1 text-xs font-medium text-indigo-500 group-hover:gap-2 transition-all">
          Read article <ArrowRight className="w-3.5 h-3.5" />
        </span>
      </div>
    </Link>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog/posts").then(r => { setPosts(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="relative min-h-screen bg-[#FAFAF8]" data-testid="blog-page">
      <Helmet>
        <title>Blog — LoomPDF Study Tips & Guides</title>
        <meta name="description" content="Study smarter with guides on turning PDFs into study decks, memorisation techniques, AI exam prep, and the best tools for students in 2026." />
        <link rel="canonical" href="https://loompdf.com/blog" />
      </Helmet>
      <Nav />

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-28 pb-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-indigo-500 font-medium mb-3">
            <BookOpen className="w-4 h-4" /> Study Resources
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-stone-900">The LoomPDF Blog</h1>
          <p className="text-stone-500 mt-3 max-w-md mx-auto">
            Study guides, memorisation techniques, and tools to help you learn faster.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
          </div>
        ) : posts.length === 0 ? (
          <p className="text-center text-stone-400 py-24">No posts yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post, i) => (
              <BlogCard key={post.slug} post={post} featured={i === 0} />
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
